import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip as MuiTooltip,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { bloomberg } from '../theme/bloombergTheme';
import MacroSparkline from './MacroSparkline';
import MacroSeriesChart from './MacroSeriesChart';
import {
  getMacroOverview,
  getMacroSeries,
  MacroOverviewSeries,
  MacroSeriesResponse,
} from '../services/api';
import {
  formatMacroValue,
  formatMacroChange,
  changeColor,
  changeDirection,
  SOURCE_LABEL,
} from '../utils/macroFormat';

const MONO = bloomberg.font.mono;

// Same horizon vocabulary the Sectors tab already uses, mapped to the change
// keys the API returns.
const HORIZONS: { label: string; key: '1d' | '1w' | '1m' | '3m' | 'ytd' | '1y' }[] = [
  { label: '1D', key: '1d' },
  { label: '1W', key: '1w' },
  { label: '1M', key: '1m' },
  { label: '3M', key: '3m' },
  { label: 'YTD', key: 'ytd' },
  { label: '1Y', key: '1y' },
];

const YEAR_RANGES = [1, 5, 10, 30];

const REFRESH_INTERVAL_MS = 10 * 60 * 1000;

// Staleness is judged against each series' own publication cadence, which the
// backend measures from the data. A flat cutoff cannot work here: 99 days is
// nothing for a quarterly print and three missed months for a monthly one.
//
// A series is stale once it has missed roughly three of its own cycles, with a
// 10-day floor so a daily feed is not flagged over a long weekend. Series with
// no measurable cadence fall back to a generous fixed window.
// Four cycles, not three: several feeds publish in arrears as a matter of
// course (the OECD monthly sovereigns run 2-3 months behind by design), and
// flagging those permanently would train the reader to ignore the marker. Four
// still catches the genuinely dead ones — the ISM mirror is 13 cycles behind.
// The floor is 14 days rather than 10 so a daily series that FRED batches out
// weekly (DTWEXBGS) is not flagged for doing exactly what it always does.
const MISSED_CYCLES_BEFORE_STALE = 4;
const MIN_STALE_DAYS = 14;
const FALLBACK_STALE_DAYS = 200;

const staleThreshold = (cadenceDays: number | null): number =>
  cadenceDays
    ? Math.max(cadenceDays * MISSED_CYCLES_BEFORE_STALE, MIN_STALE_DAYS)
    : FALLBACK_STALE_DAYS;

const isStale = (s: MacroOverviewSeries): boolean =>
  !!s.latest && daysSince(s.latest.date) > staleThreshold(s.cadence_days);

const daysSince = (iso: string): number =>
  Math.floor((Date.now() - Date.parse(iso)) / 86400000);

const relativeAge = (days: number): string => {
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 31) return `${days} days ago`;
  if (days < 365) {
    const m = Math.round(days / 30.4);
    return `${m} month${m === 1 ? '' : 's'} ago`;
  }
  const y = (days / 365).toFixed(1);
  return `${y} years ago`;
};

/** Describe a measured gap in words the reader already uses. */
const cadenceLabel = (days: number): string => {
  if (days <= 1) return 'daily';
  if (days <= 4) return 'daily (business days)';
  if (days <= 10) return 'weekly';
  if (days <= 45) return 'monthly';
  if (days <= 130) return 'quarterly';
  return `every ~${days}d`;
};

const prettyDate = (iso: string): string =>
  new Date(iso + 'T00:00:00').toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

interface DetailState {
  meta: MacroOverviewSeries;
  years: number;
  data: MacroSeriesResponse | null;
  loading: boolean;
  error: string | null;
}

const MacroCycleDashboard: React.FC = () => {
  const [series, setSeries] = useState<MacroOverviewSeries[]>([]);
  const [groups, setGroups] = useState<{ id: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [horizon, setHorizon] = useState<typeof HORIZONS[number]['key']>('1m');
  const [detail, setDetail] = useState<DetailState | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);

  const load = useCallback(async (opts: { spinner?: boolean } = {}) => {
    try {
      if (opts.spinner) setLoading(true);
      setRefreshing(true);
      const res = await getMacroOverview();
      setSeries(res.series);
      setGroups(res.groups);
      setFetchedAt(new Date());
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load macro series');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load({ spinner: true });
  }, [load]);

  // The panel used to fetch once on mount and never again, so a dashboard left
  // open — or an SPA tab that stays mounted — showed whatever was true when it
  // first rendered, indefinitely. Three triggers now cover the real cases:
  // returning to the tab, a long idle session, and an explicit ask.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    // Backstop for a dashboard left open on a second monitor. Macro data moves
    // slowly — the collector runs daily, the market overlay intraday — so this
    // is deliberately infrequent rather than a live ticker.
    const timer = window.setInterval(() => load(), REFRESH_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.clearInterval(timer);
    };
  }, [load]);

  const byGroup = useMemo(() => {
    const m = new Map<string, MacroOverviewSeries[]>();
    series.forEach((s) => {
      if (!m.has(s.group)) m.set(s.group, []);
      m.get(s.group)!.push(s);
    });
    return m;
  }, [series]);

  const loadDetail = useCallback(async (meta: MacroOverviewSeries, years: number) => {
    setDetail({ meta, years, data: null, loading: true, error: null });
    try {
      const data = await getMacroSeries(meta.id, years);
      setDetail({ meta, years, data, loading: false, error: null });
    } catch (e: any) {
      setDetail({ meta, years, data: null, loading: false, error: e?.message || 'Failed to load series' });
    }
  }, []);

  const staleCount = useMemo(() => series.filter(isStale).length, [series]);

  const asOf = useMemo(() => {
    const dates = series.map((s) => s.latest?.date).filter(Boolean) as string[];
    return dates.length ? dates.sort().slice(-1)[0] : null;
  }, [series]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={horizon}
          onChange={(_e, v) => v && setHorizon(v)}
        >
          {HORIZONS.map((h) => (
            <ToggleButton key={h.key} value={h.key} sx={{ fontFamily: MONO, fontSize: bloomberg.fontSize.caption, px: 1.5 }}>
              {h.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontFamily: MONO, fontSize: bloomberg.fontSize.caption, color: bloomberg.text.muted }}>
          {series.length} series · latest {asOf || '—'}
          {fetchedAt && ` · fetched ${fetchedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
          {staleCount > 0 && (
            <Box component="span" sx={{ color: bloomberg.status.warning, ml: 1 }}>
              · {staleCount} stale
            </Box>
          )}
        </Typography>
        <MuiTooltip title="Refresh now" arrow>
          <span>
            <IconButton size="small" onClick={() => load()} disabled={refreshing}>
              <RefreshIcon
                sx={{
                  fontSize: 16,
                  color: bloomberg.text.muted,
                  animation: refreshing ? 'macroSpin 0.9s linear infinite' : 'none',
                  '@keyframes macroSpin': { to: { transform: 'rotate(360deg)' } },
                }}
              />
            </IconButton>
          </span>
        </MuiTooltip>
      </Box>

      {/* Panels — one per catalogue group */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
        {groups.map((g) => {
          const rows = byGroup.get(g.id) || [];
          if (!rows.length) return null;
          return (
            <Paper
              key={g.id}
              sx={{
                bgcolor: bloomberg.bg.secondary,
                border: `1px solid ${bloomberg.border.primary}`,
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${bloomberg.border.primary}` }}>
                <Typography
                  sx={{
                    fontFamily: MONO,
                    fontSize: bloomberg.fontSize.micro,
                    letterSpacing: 0.8,
                    textTransform: 'uppercase',
                    color: bloomberg.accent.orange,
                    fontWeight: 600,
                  }}
                >
                  {g.label}
                </Typography>
              </Box>

              {/* Native table: click handling on <tr> is more reliable here
                  than nested MUI Box rows. */}
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {rows.map((s) => {
                    const change = s.latest?.changes?.[horizon];
                    const dir = changeDirection(change);
                    const color = changeColor(change, s.good_when);
                    const age = s.latest ? daysSince(s.latest.date) : null;
                    const stale = isStale(s);
                    const tip = (
                      <Box sx={{ py: 0.25 }}>
                        <Typography sx={{ fontSize: bloomberg.fontSize.bodyDense, fontWeight: 600, mb: 0.25 }}>
                          {s.name}
                        </Typography>
                        {s.latest ? (
                          <Typography
                            sx={{
                              fontFamily: MONO,
                              fontSize: bloomberg.fontSize.caption,
                              color: stale ? bloomberg.status.warning : 'inherit',
                            }}
                          >
                            Last updated {prettyDate(s.latest.date)} · {relativeAge(age as number)}
                            {s.cadence_days ? ` · ${cadenceLabel(s.cadence_days)}` : ''}
                            {stale ? ' · feed may be stale' : ''}
                          </Typography>
                        ) : (
                          <Typography sx={{ fontFamily: MONO, fontSize: bloomberg.fontSize.caption }}>
                            No data
                          </Typography>
                        )}
                        <Typography sx={{ fontFamily: MONO, fontSize: bloomberg.fontSize.caption, opacity: 0.8 }}>
                          {SOURCE_LABEL[s.source] || s.source}
                          {s.transform !== 'none' ? ` · ${s.transform}` : ''} · {s.unit}
                        </Typography>
                        {s.note && (
                          <Typography sx={{ fontSize: bloomberg.fontSize.caption, mt: 0.5, opacity: 0.85 }}>
                            {s.note}
                          </Typography>
                        )}
                      </Box>
                    );
                    return (
                      <MuiTooltip
                        key={s.id}
                        title={tip}
                        arrow
                        placement="top"
                        /* Slow to open and quick between rows, so dragging the
                           pointer down 11 rows does not strobe tooltips. */
                        enterDelay={450}
                        enterNextDelay={250}
                        leaveDelay={0}
                        componentsProps={{ tooltip: { sx: { maxWidth: 320 } } }}
                      >
                      <tr
                        onClick={() => loadDetail(s, 10)}
                        style={{ cursor: 'pointer', borderTop: `1px solid ${bloomberg.border.primary}` }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = bloomberg.bg.hover)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        <td style={{ padding: '6px 6px 6px 12px', width: '42%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography
                              sx={{
                                fontSize: bloomberg.fontSize.bodyDense,
                                color: bloomberg.text.primary,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {s.name}
                            </Typography>
                            {s.note && (
                              <InfoOutlinedIcon
                                sx={{ fontSize: 13, color: bloomberg.text.muted, flexShrink: 0 }}
                              />
                            )}
                          </Box>
                        </td>
                        <td style={{ padding: '4px 6px', width: 92 }}>
                          <MacroSparkline values={s.sparkline} />
                        </td>
                        <td style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              {stale && (
                                <Box
                                  component="span"
                                  sx={{
                                    width: 5,
                                    height: 5,
                                    borderRadius: '50%',
                                    bgcolor: bloomberg.status.warning,
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                              <Typography
                                sx={{
                                  fontFamily: MONO,
                                  fontSize: bloomberg.fontSize.bodyDense,
                                  color: stale ? bloomberg.text.muted : bloomberg.text.primary,
                                  fontWeight: 600,
                                }}
                              >
                              {formatMacroValue(s.latest?.value, s.unit)}
                            </Typography>
                          </Box>
                        </td>
                        <td style={{ padding: '6px 12px 6px 6px', textAlign: 'right', whiteSpace: 'nowrap', width: 96 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            {/* Arrow + number, so direction never rests on colour alone. */}
                            {dir !== 0 &&
                              (dir > 0 ? (
                                <ArrowDropUpIcon sx={{ fontSize: 16, color, ml: -0.5 }} />
                              ) : (
                                <ArrowDropDownIcon sx={{ fontSize: 16, color, ml: -0.5 }} />
                              ))}
                            <Typography sx={{ fontFamily: MONO, fontSize: bloomberg.fontSize.caption, color }}>
                              {formatMacroChange(change, s.unit)}
                            </Typography>
                          </Box>
                        </td>
                      </tr>
                      </MuiTooltip>
                    );
                  })}
                </tbody>
              </table>
            </Paper>
          );
        })}
      </Box>

      {/* Detail */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        {detail && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: bloomberg.fontSize.panelTitle, color: bloomberg.text.primary, fontWeight: 600 }}>
                    {detail.meta.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      label={SOURCE_LABEL[detail.meta.source] || detail.meta.source}
                      sx={{ height: 18, fontSize: bloomberg.fontSize.micro, fontFamily: MONO }}
                    />
                    {detail.meta.transform !== 'none' && (
                      <Chip
                        size="small"
                        label={detail.meta.transform}
                        sx={{ height: 18, fontSize: bloomberg.fontSize.micro, fontFamily: MONO }}
                      />
                    )}
                    {detail.data?.latest && (
                      <Typography sx={{ fontFamily: MONO, fontSize: bloomberg.fontSize.caption, color: bloomberg.text.muted }}>
                        {formatMacroValue(detail.data.latest.value, detail.meta.unit)} as of {detail.data.latest.date}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={detail.years}
                  onChange={(_e, v) => v && loadDetail(detail.meta, v)}
                >
                  {YEAR_RANGES.map((y) => (
                    <ToggleButton key={y} value={y} sx={{ fontFamily: MONO, fontSize: bloomberg.fontSize.micro, px: 1.25, py: 0.25 }}>
                      {y}Y
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <IconButton size="small" onClick={() => setDetail(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              {detail.loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : detail.error ? (
                <Alert severity="error">{detail.error}</Alert>
              ) : detail.data ? (
                <>
                  <MacroSeriesChart series={detail.data} height={320} />
                  {detail.meta.note && (
                    <Typography sx={{ mt: 1.5, fontSize: bloomberg.fontSize.caption, color: bloomberg.text.secondary }}>
                      {detail.meta.note}
                    </Typography>
                  )}
                  {!!detail.data.recession_bands?.length && (
                    <Typography sx={{ mt: 0.5, fontSize: bloomberg.fontSize.micro, color: bloomberg.text.muted }}>
                      Shaded bands are NBER recessions.
                    </Typography>
                  )}
                </>
              ) : null}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default MacroCycleDashboard;
